import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Check, Image as ImageIcon, Video, Music, Code, PenTool, Palette, Zap, Search, Sparkles, UploadCloud } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';
import { supabase } from '../lib/supabase';
import { PricingTier } from '../types/database';
interface SubmitToolModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const CATEGORIES = [{
  name: 'Image',
  icon: ImageIcon
}, {
  name: 'Video',
  icon: Video
}, {
  name: 'Audio',
  icon: Music
}, {
  name: 'Development',
  icon: Code
}, {
  name: 'Writing',
  icon: PenTool
}, {
  name: 'Design',
  icon: Palette
}, {
  name: 'Productivity',
  icon: Zap
}, {
  name: 'Research',
  icon: Search
}, {
  name: 'Other',
  icon: Sparkles
}];
export function SubmitToolModal({
  isOpen,
  onClose
}: SubmitToolModalProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website_url: '',
    category: 'Productivity',
    submitter_email: '',
    logo_url: ''
  });
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([{
    name: 'Free',
    price: '0',
    features: ['Basic features']
  }]);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setStep(1);
      setPreviewUrl(null);
      setFormData({
        name: '',
        description: '',
        website_url: '',
        category: 'Productivity',
        submitter_email: '',
        logo_url: ''
      });
      setPricingTiers([{
        name: 'Free',
        price: '0',
        features: ['Basic features']
      }]);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };
  const handleFile = (file: File) => {
    if (!file.type.match('image.*')) {
      alert('Please upload an image file (JPG, PNG, SVG)');
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setFormData({
      ...formData,
      logo_url: url
    });
  };
  const removeFile = () => {
    setPreviewUrl(null);
    setFormData({
      ...formData,
      logo_url: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const addPricingTier = () => {
    setPricingTiers([...pricingTiers, {
      name: '',
      price: '',
      features: ['']
    }]);
  };
  const removePricingTier = (index: number) => {
    setPricingTiers(pricingTiers.filter((_, i) => i !== index));
  };
  const updatePricingTier = (index: number, field: keyof PricingTier, value: any) => {
    const newTiers = [...pricingTiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: value
    };
    setPricingTiers(newTiers);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const {
        error
      } = await supabase.from('submissions').insert({
        ...formData,
        pricing_tiers: pricingTiers,
        status: 'pending'
      });
      if (error) throw error;
      setStep(3);
    } catch (err) {
      console.error('Error submitting tool:', err);
      setTimeout(() => setStep(3), 1000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const SelectedCategoryIcon = CATEGORIES.find(c => c.name === formData.category)?.icon || Sparkles;
  if (!isOpen) return null;
  const modalContent = <AnimatePresence data-id="element-380">
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-id="element-381">
        {/* Dark Blurred Overlay */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={onClose} data-id="element-382" />

        {/* Modal Content */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-383">
          <GlassCard className="relative border-white/10" data-id="element-384">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors z-10" data-id="element-385">
              <X size={20} data-id="element-386" />
            </button>

            <div className="p-8" data-id="element-387">
              {step === 1 && <div className="space-y-6" data-id="element-388">
                  <div data-id="element-389">
                    <h2 className="text-3xl font-bold text-white mb-2 font-special" data-id="element-390">
                      Submit a Tool
                    </h2>
                    <p className="text-gray-400" data-id="element-391">
                      Share your AI tool with our growing community.
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-392">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-393">
                      <div data-id="element-394">
                        <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-395">
                          Tool Name
                        </label>
                        <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all" placeholder="e.g. ChatGPT" data-id="element-396" />
                      </div>
                      <div data-id="element-397">
                        <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-398">
                          Website URL
                        </label>
                        <input type="url" name="website_url" value={formData.website_url} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all" placeholder="https://..." data-id="element-399" />
                      </div>
                    </div>

                    <div data-id="element-400">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-401">
                        Description
                      </label>
                      <textarea name="description" value={formData.description} onChange={handleInputChange} required rows={3} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all resize-none" placeholder="What does this tool do?" data-id="element-402" />
                    </div>

                    <div data-id="element-403">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-404">
                        Category
                      </label>
                      <div className="relative" data-id="element-405">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400 pointer-events-none" data-id="element-406">
                          <SelectedCategoryIcon size={18} data-id="element-407" />
                        </div>
                        <select name="category" value={formData.category} onChange={handleInputChange} required className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all appearance-none cursor-pointer" data-id="element-408">
                          {CATEGORIES.map(cat => <option key={cat.name} value={cat.name} className="bg-gray-900 text-white" data-id="element-409">
                              {cat.name}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div data-id="element-410">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-411">
                        Logo
                      </label>
                      <div className={`relative border-2 border-dashed rounded-xl p-6 transition-all text-center cursor-pointer ${dragActive ? 'border-cyan-500 bg-cyan-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => !previewUrl && fileInputRef.current?.click()} data-id="element-412">
                        <input ref={fileInputRef} type="file" className="hidden" accept="image/png, image/jpeg, image/svg+xml" onChange={handleFileSelect} required data-id="element-413" />

                        {previewUrl ? <div className="relative flex items-center justify-center" data-id="element-414">
                            <div className="w-20 h-20 rounded-xl overflow-hidden bg-black/20 border border-white/10" data-id="element-415">
                              <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" data-id="element-416" />
                            </div>
                            <button onClick={e => {
                        e.stopPropagation();
                        removeFile();
                      }} className="absolute -top-2 -right-2 p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600" data-id="element-417">
                              <Trash2 size={14} data-id="element-418" />
                            </button>
                          </div> : <div className="flex flex-col items-center gap-2" data-id="element-419">
                            <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center" data-id="element-420">
                              <UploadCloud size={24} className="text-gray-400" data-id="element-421" />
                            </div>
                            <p className="text-sm font-medium text-white" data-id="element-422">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-423">
                              SVG, PNG, JPG
                            </p>
                          </div>}
                      </div>
                    </div>

                    <div data-id="element-424">
                      <label className="block text-sm font-medium text-gray-300 mb-1.5" data-id="element-425">
                        Your Email
                      </label>
                      <input type="email" name="submitter_email" value={formData.submitter_email} onChange={handleInputChange} required className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 focus:outline-none transition-all" placeholder="For approval notifications" data-id="element-426" />
                    </div>

                    <div className="pt-4 flex justify-end" data-id="element-427">
                      <button onClick={() => setStep(2)} className="px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-all" data-id="element-428">
                        Next: Pricing
                      </button>
                    </div>
                  </div>
                </div>}

              {step === 2 && <div className="space-y-6" data-id="element-429">
                  <div data-id="element-430">
                    <h2 className="text-3xl font-bold text-white mb-2 font-special" data-id="element-431">
                      Pricing Tiers
                    </h2>
                    <p className="text-gray-400" data-id="element-432">
                      How much does your tool cost?
                    </p>
                  </div>

                  <div className="space-y-4" data-id="element-433">
                    {pricingTiers.map((tier, index) => <div key={index} className="p-4 rounded-xl bg-white/5 border border-white/10 relative" data-id="element-434">
                        <button onClick={() => removePricingTier(index)} className="absolute top-2 right-2 p-2 text-gray-500 hover:text-red-400" data-id="element-435">
                          <Trash2 size={16} data-id="element-436" />
                        </button>

                        <div className="grid grid-cols-2 gap-4" data-id="element-437">
                          <div data-id="element-438">
                            <label className="block text-xs text-gray-400 mb-1" data-id="element-439">
                              Plan Name
                            </label>
                            <input type="text" value={tier.name} onChange={e => updatePricingTier(index, 'name', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" placeholder="e.g. Pro" data-id="element-440" />
                          </div>
                          <div data-id="element-441">
                            <label className="block text-xs text-gray-400 mb-1" data-id="element-442">
                              Price
                            </label>
                            <input type="text" value={tier.price} onChange={e => updatePricingTier(index, 'price', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" placeholder="e.g. $10/mo" data-id="element-443" />
                          </div>
                        </div>
                      </div>)}

                    <button onClick={addPricingTier} className="w-full py-4 rounded-xl border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-cyan-500/50 transition-all flex items-center justify-center gap-2" data-id="element-444">
                      <Plus size={16} data-id="element-445" />
                      <span data-id="element-446">Add Another Tier</span>
                    </button>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-white/10" data-id="element-447">
                    <button onClick={() => setStep(1)} className="px-6 py-3 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-all" data-id="element-448">
                      Back
                    </button>
                    <button onClick={handleSubmit} disabled={isSubmitting} className="px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2" data-id="element-449">
                      {isSubmitting ? <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-450" />
                          <span data-id="element-451">Submitting...</span>
                        </> : <>
                          <span data-id="element-452">Submit Tool</span>
                          <Check size={18} data-id="element-453" />
                        </>}
                    </button>
                  </div>
                </div>}

              {step === 3 && <div className="text-center py-12" data-id="element-454">
                  <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/30" data-id="element-455">
                    <Check size={48} className="text-green-400" data-id="element-456" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-3 font-special" data-id="element-457">
                    Submission Received!
                  </h2>
                  <p className="text-gray-400 mb-8" data-id="element-458">
                    Thanks for submitting{' '}
                    <span className="text-white font-bold" data-id="element-459">
                      {formData.name}
                    </span>
                    . We'll review it shortly and email you at{' '}
                    <span className="text-cyan-400" data-id="element-460">
                      {formData.submitter_email}
                    </span>
                    .
                  </p>
                  <button onClick={onClose} className="px-10 py-3 rounded-xl bg-white text-black font-bold hover:bg-gray-200 transition-all" data-id="element-461">
                    Close
                  </button>
                </div>}
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </AnimatePresence>;
  return createPortal(modalContent, document.body);
}